import { Box, Button, Grid, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stampToDate } from '../../_helpers/dateTime'
import { IBookingSuccessInformation, IRecurringBooking } from '../../_models/newBookingInterface'
import SpanwithColor from '../../components/chip/colorSpan'
import ShowMoreContainer from '../../components/common/showMoreItem'
import { Tick2Icon } from '../../components/icons'

interface BookingDetailsModalProps {
  bookingSuccessInformation: IBookingSuccessInformation | Record<string, never>
  onClose: () => void
}

const BookingDetailsModal = ({
  bookingSuccessInformation = {},
  onClose,
}: BookingDetailsModalProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [successfulBookings, setSuccessfulBookings] = useState<string[]>([])
  const [failedBookings, setFailedBookings] = useState<string[]>([])
  const [seatNames, setSeatNames] = useState<string[]>([])
  useEffect(() => {
    if (bookingSuccessInformation?.recurring_booking_data?.length) {
      const successful: string[] = []
      const failed: string[] = []

      bookingSuccessInformation.recurring_booking_data?.forEach(
        (individualBooking: IRecurringBooking) => {
          if (individualBooking.status === 'success') {
            successful.push(stampToDate(individualBooking.date))
          } else if (individualBooking.status === 'failed') {
            failed.push(stampToDate(individualBooking.date))
          }
        },
      )

      setSuccessfulBookings(successful)
      setFailedBookings(failed)

      // Extract seat names from booked_seats array
      const bookedSeatsInfo = bookingSuccessInformation?.recurring_booking_data[0]?.booked_seats
      const seatNamesList = bookedSeatsInfo?.map((seat) => seat?.seat_name) || []
      setSeatNames(seatNamesList)
    }
  }, [bookingSuccessInformation])

  return (
    <>
      {!bookingSuccessInformation?.recurring_booking_data?.length ? (
        <>
          {!bookingSuccessInformation?.book_for_others && (
            <Box sx={{ display: { md: 'flex' } }} justifyContent={'center'}>
              <Box mr={{ xs: 0, md: 3 }} mt={1} ml={{ xs: 0, md: 2 }} mb={1}>
                <SpanwithColor
                  bgcolor='pagination.contrastText'
                  borderCol='solid 0.063rem'
                  brdrCol='info.light'
                >
                  <Typography
                    color='toggleButtonGroup.main'
                    variant='subtitle1'
                    pl={{ xs: 0, md: 1 }}
                  >
                    {t('newBooking.tokenWithColon')}
                  </Typography>
                  <Typography
                    color='common.black'
                    variant='subtitle1'
                    pr={{ xs: 0, md: 3 }}
                    pl={{ xs: 0, sm: 1, md: 1 }}
                  >
                    {bookingSuccessInformation?.short_token}
                  </Typography>
                </SpanwithColor>
              </Box>
              <Box mt={1} mb={1}>
                <SpanwithColor
                  bgcolor='pagination.contrastText'
                  borderCol='solid 0.063rem '
                  brdrCol='info.light'
                >
                  <Typography
                    color='toggleButtonGroup.main'
                    variant='subtitle1'
                    pl={{ xs: 0, md: 1 }}
                  >
                    {t('newBooking.numberOfSeatsWithColon')}
                  </Typography>
                  <Typography
                    color='common.black'
                    variant='subtitle1'
                    pr={{ xs: 0, md: 3 }}
                    pl={{ xs: 0, sm: 1, md: 1 }}
                  >
                    {bookingSuccessInformation?.no_of_seats}
                  </Typography>
                </SpanwithColor>
              </Box>
            </Box>
          )}

          {bookingSuccessInformation?.seats && bookingSuccessInformation?.seats?.length > 0 && (
            <Box
              mt={2}
              sx={{
                padding: '.85rem 1.313rem .85rem 1.188rem',
                borderRadius: '0.625rem',
                backgroundColor: 'table.main',
              }}
            >
              <Box sx={{ display: { md: 'flex' }, alignItems: 'center' }}>
                <Typography
                  color='toggleButtonGroup.main'
                  variant='subtitle1'
                  pr={{ xs: 0, md: 1.5 }}
                >
                  {t('newBooking.selectedSeatsWithColon')}
                </Typography>
                <Box flex={4} display={'flex'}>
                  <ShowMoreContainer
                    colorOfItem={'iconColors.main'}
                    colorOfOption={'error.main'}
                    fontSizeOfItem={'0.875rem'}
                    fontWeightOfOption='normal'
                    itemsToShowInitially={4}
                    isItemLink={false}
                    mtRemove={true}
                    data={bookingSuccessInformation.seats
                      .split(', ')
                      .map((seat: string) => seat.trim())}
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box
            mt={2}
            sx={{
              padding: '1.063rem 1.313rem 1.063rem 1.188rem',
              borderRadius: '0.625rem',
              backgroundColor: 'table.main',
            }}
          >
            {bookingSuccessInformation?.tokens?.map(
              (item: { token_id: string; customer_email: string }) => (
                <Box
                  mt={1}
                  sx={{ display: { md: 'flex' } }}
                  mb={1}
                  alignItems={'end'}
                  key={item.token_id}
                >
                  <Box sx={{ display: { xs: 'flex' } }}>
                    <Box pr={1}>
                      <Tick2Icon
                        fill={theme.palette.error.contrastText}
                        width='10'
                        height='8'
                        stoke={theme.palette.table.main}
                      />
                    </Box>
                    <Typography
                      fontSize={'1rem'}
                      color='common.black'
                      variant='subtitle1'
                      pr={{ xs: 0, md: 1.5 }}
                    >
                      {t('newBooking.bookingIdLabel', { value: item.token_id })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      color='toggleButtonGroup.main'
                      variant='subtitle1'
                      pr={{ xs: 0, md: 1 }}
                      pl={{ xs: 2.2, md: 0 }}
                    >
                      {stampToDate(bookingSuccessInformation?.selected_date)} :
                    </Typography>
                  </Box>
                  <Box>
                    <Typography color='tab.dark' variant='subtitle1' pr={0} pl={{ xs: 2.2, md: 0 }}>
                      <span dir='ltr'>{bookingSuccessInformation?.selectedSlot}</span>
                    </Typography>
                  </Box>
                </Box>
              ),
            )}
          </Box>
        </>
      ) : (
        <>
          <Box
            mt={1}
            sx={{
              padding: '1.063rem 1.313rem 1.063rem 1.188rem',
              borderRadius: '0.625rem',
              backgroundColor: 'table.main',
              width: '100%',
            }}
          >
            <Box display='flex'>
              <Typography color='tab.dark' variant='subtitle1' pr={{ xs: 0, md: 1.5 }} flex={2}>
                {t('newBooking.selectedSlot')}
              </Typography>
              <Typography color='tab.dark' variant='subtitle1' flex={0.5}>
                :
              </Typography>
              <Typography color='toggleButtonGroup.main' variant='subtitle1' flex={4}>
                {bookingSuccessInformation?.recurring_booking_data[0]?.selected_slot}
              </Typography>
            </Box>
            <Box display='flex'>
              <Typography flex={2} color='tab.dark' variant='subtitle1' pr={{ xs: 0, md: 1.5 }}>
                {t('newBooking.noOfSeats')}
              </Typography>
              <Typography color='tab.dark' variant='subtitle1' flex={0.5}>
                :
              </Typography>
              <Typography color='toggleButtonGroup.main' variant='subtitle1' flex={4}>
                {bookingSuccessInformation?.recurring_booking_data[0]?.no_of_seats}
              </Typography>
            </Box>
            {seatNames?.length > 0 && (
              <Box display='flex'>
                <Typography flex={2} color='tab.dark' variant='subtitle1' pr={{ xs: 0, md: 1.5 }}>
                  {t('newBooking.selectedSeatsText')}
                </Typography>
                <Typography color='tab.dark' variant='subtitle1' flex={0.5}>
                  :
                </Typography>
                <Typography color='toggleButtonGroup.main' variant='subtitle1' flex={4}>
                  {seatNames.join(', ')}
                </Typography>
              </Box>
            )}
          </Box>

          <Box mt={3}>
            {successfulBookings?.length > 0 && (
              <Box textAlign={'center'}>
                <Typography variant='subtitle1' color='tab.dark'>
                  {t('newBooking.bookingSuccessfulForText')}
                </Typography>
                <Typography variant='subtitle1' color='toggleButtonGroup.main'>
                  {successfulBookings.join(', ')}
                </Typography>
              </Box>
            )}
            {failedBookings?.length > 0 && (
              <Box mt={2} textAlign={'center'}>
                <Typography variant='subtitle1' color='tab.dark'>
                  {t('newBooking.bookingFailedForText')}
                </Typography>
                <Typography variant='subtitle1' color='toggleButtonGroup.main'>
                  {failedBookings.join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      <Box>
        <Grid container columnSpacing={'1.25rem'}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={4}>
            <Button
              type='submit'
              sx={{ width: '100%' }}
              variant='contained'
              size='large'
              onClick={onClose}
            >
              {t('newBooking.okayBtn')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      {bookingSuccessInformation.is_recurring_booking && (
        <Typography textAlign='center' variant='subtitle2' color='secondary.dark' mt={4} px={5}>
          {t('newBooking.bookingSuccessInfoText1')}&nbsp;
          {t('newBooking.bookingSuccessInfoText3')}&nbsp;
          {`${t('newBooking.bookingSuccessInfoText2')}.`}
        </Typography>
      )}
    </>
  )
}

export default BookingDetailsModal
